import { createTheme } from '@mui/material/styles';

const FOREST   = '#166534';
const EMERALD  = '#10b981';
const SLATE_900 = '#0f172a';

const theme = createTheme({
  palette: {
    primary: {
      main:         FOREST,
      light:        '#22c55e',
      dark:         '#14532d',
      contrastText: '#ffffff',
    },
    secondary: {
      main:         EMERALD,
      light:        '#34d399',
      dark:         '#059669',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fafaf9',
      paper:   '#ffffff',
    },
    text: {
      primary:   '#1c1917',
      secondary: '#78716c',
    },
    divider: '#e7e5e4',
    success: {
      main:         '#059669',
      light:        '#34d399',
      dark:         '#047857',
      contrastText: '#ffffff',
    },
    warning: {
      main:         '#d97706',
      light:        '#fbbf24',
      dark:         '#b45309',
      contrastText: '#ffffff',
    },
    error: {
      main:         '#dc2626',
      light:        '#f87171',
      dark:         '#b91c1c',
      contrastText: '#ffffff',
    },
    info: {
      main:         '#2563eb',
      light:        '#60a5fa',
      dark:         '#1d4ed8',
      contrastText: '#ffffff',
    },
  },

  typography: {
    fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.015em' },
    h4: { fontWeight: 700, letterSpacing: '-0.015em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600, letterSpacing: '-0.005em' },
    body1: { letterSpacing: '-0.005em' },
    body2: { letterSpacing: '-0.005em' },
    button: { fontWeight: 600, letterSpacing: '-0.01em' },
    caption: { letterSpacing: '0' },
    overline: { fontWeight: 600, letterSpacing: '0.08em' },
  },

  shape: {
    borderRadius: 10,
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          scrollbarColor: '#d6d3d1 transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: '#d6d3d1',
            borderRadius: 3,
          },
        },
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'default' },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e7e5e4',
          color: '#1c1917',
          boxShadow: 'none',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          letterSpacing: '-0.01em',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
          '&:active': { boxShadow: 'none' },
        },
        outlined: {
          borderColor: '#e7e5e4',
          '&:hover': { borderColor: '#d6d3d1', backgroundColor: '#fafaf9' },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          border: '1px solid #e7e5e4',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        elevation1: {
          border: '1px solid #e7e5e4',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        },
        elevation2: {
          boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
      },
    },

    MuiTextField: {
      defaultProps: { size: 'small' },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          letterSpacing: '-0.005em',
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#e7e5e4',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d6d3d1',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: '0.75rem',
          letterSpacing: '-0.005em',
          borderRadius: 6,
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#fafaf9',
            fontWeight: 600,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#78716c',
            borderBottom: '1px solid #e7e5e4',
            paddingTop: 10,
            paddingBottom: 10,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #f5f4f2',
          fontSize: '0.875rem',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: '#fafaf9' },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#e7e5e4' },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontSize: '0.875rem',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: '1px solid #e7e5e4',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: SLATE_900,
          fontSize: '0.75rem',
          borderRadius: 6,
          padding: '5px 10px',
        },
        arrow: { color: SLATE_900 },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { fontSize: '0.875rem' },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          borderRadius: 6,
          mx: 0.5,
        },
      },
    },
  },
});

export default theme;
